import { AitLocation, classMerge } from "aic";
import { AnnotationContextWindow } from "components/annotations/AnnotationContextWindow";
import { AnnotationOptionType } from "components/annotations/interface";
import { TlfContext } from "contexts/TlfContext";
import { ReactNode, useContext, useRef, useState } from "react";
import { PencilSquare, PlusCircle } from "react-bootstrap-icons";
import styles from "./AnnotationButton.module.css";
import { AnnotationPreviewBubble } from "./AnnotationPreviewBubble";
import { AnnotationWhereGroup } from "./AnnotationWhereGroup";

/**
 * Props for the AnnotationButton component.
 *
 * @interface AnnotationButtonProps
 * @property existingAnnotation - Existing annotation data if editing an annotation.
 * @property annotationType - Type of annotation (e.g., "Note", "Footnote").
 * @property annotationLocation - Location in the table where the annotation applies.
 * @property buttonId - Optional ID for the button element.
 * @property hidden - Whether the button should be hidden.
 * @property showPreview - Whether to show the annotation preview bubble.
 * @property repositionKey - Optional key to force preview bubble repositioning on structural changes.
 */
export interface AnnotationButtonProps {
  existingAnnotation?: AnnotationWhereGroup;
  annotationType: AnnotationOptionType;
  annotationLocation?: AitLocation;
  buttonId?: string;
  hidden?: boolean;
  showPreview?: boolean;
  repositionKey?: string | number;
}

/**
 * Button that opens the annotation modal for a specific location.
 * Shows a preview bubble when requested and adapts its icon based on editability.
 *
 * @param props - Destructured parameter object
 * @param props.existingAnnotation - Existing annotation, if any
 * @param props.annotationType - Target type for the annotation
 * @param props.annotationLocation - Optional table location for the annotation
 * @param props.buttonId - Optional id attribute for the button
 * @param props.hidden - Hide the control when true
 * @param props.showPreview - Show a preview bubble when true
 * @param props.repositionKey - Key to trigger repositioning of the preview bubble
 */
export const AnnotationButton = ({
  existingAnnotation,
  annotationType,
  annotationLocation,
  buttonId,
  hidden = false,
  showPreview = false,
  repositionKey,
}: AnnotationButtonProps): ReactNode => {
  const ref = useRef<HTMLButtonElement | null>(null);
  const [showAnnotationModal, setShowAnnotationModal] = useState(false);
  const annotationActionText = !existingAnnotation
    ? `Add ${annotationType} annotation`
    : `Edit ${annotationType} annotation`;
  const tlfContext = useContext(TlfContext);
  const canEdit = tlfContext ? !tlfContext.readOnly : true;

  // Determine bubble position based on annotation type
  const bubblePosition =
    annotationType === "Column" ? "top" : annotationType === "Row" ? "right" : "left";

  return hidden ? null : (
    <>
      <button
        id={buttonId}
        type="button"
        title={annotationActionText}
        aria-label={annotationActionText}
        className={classMerge([
          styles.annotationBtn,
          !existingAnnotation ? styles.empty : undefined,
        ])}
        onClick={() => setShowAnnotationModal(true)}
        style={{ position: "relative" }}
        ref={ref}
      >
        <span
          className={styles.glyph}
          aria-hidden="true"
        >
          <div className={styles.icon}>
            <span className={styles.letter}>A</span>
            {canEdit && (
              <span className={styles.hoverIcon}>
                {!existingAnnotation ? <PlusCircle size={12} /> : <PencilSquare size={12} />}
              </span>
            )}
          </div>
        </span>
        {showPreview && (
          <AnnotationPreviewBubble
            annotation={existingAnnotation}
            position={bubblePosition}
            target={ref}
            repositionKey={repositionKey}
          />
        )}
      </button>

      <AnnotationContextWindow
        isOpen={showAnnotationModal}
        onClose={() => setShowAnnotationModal(false)}
        annotationLocation={annotationLocation}
        annotationType={annotationType}
        contextAnnotation={existingAnnotation}
        disabled={!canEdit}
      />
    </>
  );
};

AnnotationButton.displayName = "AnnotationButton";
